import { createStyledContext, getToken, getTokenValue, isWeb, styled, Text, View, withStaticProperties } from "@tamagui/core";
import { useEffect, useState } from "react";
import { Icon } from "../Icon/Icon.mjs";
import { LoadingIndicator } from "./Button.LoadingIndicator";
import { jsx, jsxs } from "react/jsx-runtime";
const iconOnlyVariants = ["discreet", "secondary"],
  ButtonContext = createStyledContext({
    iconPosition: "right",
    internalDisabled: !1,
    variant: "primary"
  }),
  ButtonFrame = styled(View, {
    accessible: !0,
    alignItems: "center",
    animateOnly: ["background-color", "backgroundColor"],
    animation: "easeInFast",
    context: ButtonContext,
    cursor: "pointer",
    flexDirection: "row",
    focusable: !0,
    group: "button",
    justifyContent: "space-between",
    // This name has an impact on the generated css class name. If you change it, please also update the css file.
    name: "ShineButton",
    role: "button",
    tabIndex: 0,
    tag: "button",
    variants: {
      hugContent: {
        ":boolean": (v, {
          props
        }) => {
          if (v) {
            const iconPosition = "iconPosition" in props ? props.iconPosition : "right",
              variant = "variant" in props ? props.variant : "primary";
            return {
              alignSelf: "flex-start",
              justifyContent: "flex-start",
              maxWidth: "100%",
              width: "auto",
              ...(("withIcon" in props ? props.withIcon : !1) ? {
                [iconPosition === "left" ? "paddingLeft" : "paddingRight"]: `$button.horizontalPadding.${variant}.hugContentWithIcon`
              } : {})
            };
          } else return {
            maxWidth: "100%",
            width: "100%"
          };
        }
      },
      iconOnly: {
        true: {
          aspectRatio: 1,
          gap: 0,
          justifyContent: "center",
          paddingHorizontal: 0
        }
      },
      internalDisabled: {
        true: (_, {
          props
        }) => {
          const variant = "variant" in props ? props.variant : "primary";
          return {
            "aria-disabled": !0,
            backgroundColor: `$button.${variant}.background.disabled`,
            // Web only: Set the container color in order to change the icon color.
            color: `$button.${variant}.text.disabled`,
            cursor: "default",
            focusable: !1,
            pointerEvents: "none"
          };
        }
      },
      variant: {
        ":string": variant => ({
          backgroundColor: `$button.${variant}.background`,
          borderRadius: "$button.default",
          // Web only: Set the container color in order to change the icon color.
          color: `$button.${variant}.text`,
          gap: `$button.gap.${variant}`,
          height: `$button.${variant}`,
          hoverStyle: {
            backgroundColor: `$button.${variant}.background.hover`,
            // Web only: Set the container color in order to change the icon color.
            color: `$button.${variant}.text.hover`
          },
          paddingBottom: `$button.bottomPadding.${variant}`,
          paddingHorizontal: `$button.horizontalPadding.${variant}`,
          pressStyle: {
            backgroundColor: `$button.${variant}.background.press`,
            boxShadow: "none",
            // Web only: Set the container color in order to change the icon color.
            color: `$button.${variant}.text.press`
          }
        })
      },
      withIcon: {
        false: {
          justifyContent: "center"
        }
      }
    }
  }),
  ButtonText = styled(Text, {
    animateOnly: ["color"],
    animation: "easeInFast",
    context: ButtonContext,
    flexGrow: 0,
    flexShrink: 1,
    fontFamily: "$body",
    fontSize: "$regular",
    fontWeight: "500",
    lineHeight: "$regular",
    name: "ButtonText",
    numberOfLines: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    variants: {
      internalDisabled: {},
      variant: (variant, {
        props
      }) => ("internalDisabled" in props ? props.internalDisabled : !1) ? {
        color: `$button.${variant}.text.disabled`,
        fontSize: variant === "discreet" ? "$small" : "$regular",
        lineHeight: variant === "discreet" ? "$small" : "$regular"
      } : {
        "$group-button-hover": {
          color: `$button.${variant}.text.hover`
        },
        "$group-button-press": {
          color: `$button.${variant}.text.press`
        },
        color: `$button.${variant}.text`,
        fontSize: variant === "discreet" ? "$small" : "$regular",
        lineHeight: variant === "discreet" ? "$small" : "$regular"
      }
    },
    whiteSpace: "nowrap"
  }),
  TertiaryIndicator = styled(View, {
    animateOnly: ["background-color"],
    animation: "easeInFast",
    bottom: 0,
    context: ButtonContext,
    height: "$button.tertiaryIndicator.height",
    left: 0,
    position: "absolute",
    right: 0,
    variants: {
      internalDisabled: {},
      variant: (variant, {
        props
      }) => {
        const disabled = "internalDisabled" in props ? props.internalDisabled : !1;
        return variant !== "tertiary" ? {
          display: "none"
        } : disabled ? {
          backgroundColor: `$button.${variant}.text.disabled`
        } : {
          "$group-button-hover": {
            backgroundColor: `$button.${variant}.text.hover`
          },
          "$group-button-press": {
            backgroundColor: `$button.${variant}.text.press`
          },
          backgroundColor: `$button.${variant}.text`
        };
      }
    }
  }),
  IconPlaceholder = styled(View, {
    context: ButtonContext,
    // eslint-disable-next-line sort-keys-fix/sort-keys-fix
    aspectRatio: 1,
    variants: {
      variant: {
        danger: {
          height: "$button.danger.iconSize"
        },
        discreet: {
          height: "$button.discreet.iconSize"
        },
        primary: {
          height: "$button.primary.iconSize"
        },
        secondary: {
          height: "$button.secondary.iconSize"
        },
        tertiary: {
          height: "$button.tertiary.iconSize"
        }
      }
    }
  }),
  ButtonIcon = styled(Icon, {
    context: ButtonContext,
    flexShrink: 0,
    variants: {
      // every component consuming ButtonContext must implement props in order to use them
      internalDisabled: {},
      variant: {
        ":string": (variant, {
          props
        }) => {
          const iconSize = getTokenValue(`$button.${variant}.iconSize`, "size");
          return isWeb ? {
            size: iconSize
          } : ("internalDisabled" in props ? props.internalDisabled : !1) ? {
            color: `$button.${variant}.text.disabled`,
            size: iconSize
          } : {
            "$group-button-hover": {
              color: `$button.${variant}.text.hover`
            },
            "$group-button-press": {
              color: `$button.${variant}.text.press`
            },
            color: `$button.${variant}.text`,
            size: iconSize
          };
        }
      }
    }
  }, {}),
  ButtonWithContext = withStaticProperties(ButtonFrame, {
    Icon: ButtonIcon,
    IconPlaceholder,
    Indicator: TertiaryIndicator,
    Props: ButtonContext.Provider,
    Text: ButtonText
  }),
  IconContainer = ({
    hugContent,
    icon,
    iconOnly,
    isPlaceholder
  }) =>
  // no icon is defined
  !icon && !isPlaceholder ||
  // if it's the placeholder icon and there is no text
  iconOnly && isPlaceholder ||
  // if it's the placeholder icon and the button fits its content
  isPlaceholder && hugContent ? null : isPlaceholder ? /* @__PURE__ */jsx(ButtonWithContext.IconPlaceholder, {}) : /* @__PURE__ */jsx(ButtonWithContext.Icon, {
    icon
  }),
  ButtonToExport = ({
    children,
    hugContent = !1,
    icon,
    iconPosition = "right",
    isDisabled = !1,
    isLoading = !1,
    isLoadingDeferred = !1,
    variant = "primary",
    ...props
  }) => {
    const hugContentBasedOnVariant = variant === "discreet" || variant === "tertiary" ? !0 : hugContent,
      hasIcon = isLoading || !!icon,
      iconOnly = !!(!children && hasIcon),
      computedDisabled = isLoading || isDisabled;
    if (__DEV__ && iconOnly && !iconOnlyVariants.includes(variant)) throw new Error('Icon only buttons can only use the "discreet" or "secondary" variants.');
    const [showLoader, setShowLoader] = useState(!1);
    useEffect(() => {
      if (isLoading && isLoadingDeferred) {
        const timer = setTimeout(() => {
          setShowLoader(!0);
        }, 500);
        return () => clearTimeout(timer);
      } else return;
    }, [isLoading, isLoadingDeferred]);
    let iconContent;
    return isLoading ? isLoadingDeferred && !showLoader ? iconContent = /* @__PURE__ */jsx(IconContainer, {
      hugContent: hugContentBasedOnVariant,
      icon,
      iconOnly,
      isPlaceholder: !icon
    }) : iconContent = /* @__PURE__ */jsx(LoadingIndicator, {
      variant
    }) : iconContent = /* @__PURE__ */jsx(IconContainer, {
      hugContent: hugContentBasedOnVariant,
      icon,
      iconOnly,
      isPlaceholder: hasIcon && iconPosition === "left"
    }), /* @__PURE__ */jsxs(ButtonWithContext, {
      hugContent: iconOnly || hugContentBasedOnVariant,
      iconPosition,
      variant,
      withIcon: hasIcon,
      ...props,
      focusVisibleStyle: {
        // @ts-expect-error boxShadow is correctly spreaded to the component
        boxShadow: `0 0 0 2px white, 0 0 0 4px ${getToken("$outline", "color")}`,
        outline: 0
      },
      iconOnly,
      internalDisabled: computedDisabled,
      children: [variant === "tertiary" ? /* @__PURE__ */jsx(ButtonWithContext.Indicator, {}) : null, isLoading && !iconOnly && !hugContentBasedOnVariant ? /* @__PURE__ */jsx(ButtonWithContext.IconPlaceholder, {}) : /* @__PURE__ */jsx(IconContainer, {
        hugContent: hugContentBasedOnVariant,
        icon,
        iconOnly,
        isPlaceholder: icon && iconPosition === "right"
      }), /* @__PURE__ */jsx(ButtonWithContext.Text, {
        children
      }), iconContent]
    });
  },
  Button = ButtonToExport;
ButtonToExport.displayName = "Button";
export { Button };
//# sourceMappingURL=Button.mjs.map
